import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { BaseToastComponent } from '../../../@commons/ui-components/toast-popup/base-toast-component';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { ChoiceOption } from '../../../@commons/ui-components/choice-selector/choice-selector.component';
import { CircleUserIconComponent } from '../../../@icons/circle-user-icon.component';
import { LoadingIconComponent } from '../../../@icons/loading-icon.component';
import { userInitial } from '../../../@commons/utils/user-display-name.util';
import { User } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/user.entity';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import { UpdateUserRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/user/update-user.request';
import { FileApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/file/file-api';
import { UpdateExternalFileRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/dms/update-external-file-request';
import { UploadFileRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/dms/upload-file-request';
import { UploadFileResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/dms/upload-file-response';
import { FileType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/file-type.enum';
import { ExpertiseType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/expertise-type.enum';

/** Longueur maximale du nom, celle que le serveur accepte (`UpdateUserRequest.pseudo`). */
export const PSEUDO_MAX_LENGTH = 25;

/**
 * Feuille « Modifier mes infos » (P4.1) : la photo, le nom affiché et le niveau
 * de jardinier.
 *
 * - La photo s'envoie dès qu'elle est choisie (pour l'aperçu), mais ne se
 *   rattache au compte qu'à l'enregistrement : fermer la feuille ne change rien.
 * - Le nom est obligatoire et tient en 25 caractères (règle du serveur) ; le
 *   bouton n'est jamais grisé, il dit ce qui manque sous le champ.
 * - La langue n'est plus ici : elle a sa feuille (P4.2) et un seul endroit.
 */
@Component({
  selector: 'vw-edit-user-toast',
  imports: [SharedModule, FormsModule, TranslatePipe, CircleUserIconComponent, LoadingIconComponent],
  templateUrl: './edit-user-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: UserApi, useClass: UserApi },
    { provide: FileApi, useClass: FileApi },
  ],
})
export class EditUserToastComponent extends BaseToastComponent implements OnChanges {
  @Input() user: User | null = null;

  readonly pseudoMaxLength = PSEUDO_MAX_LENGTH;
  readonly expertiseOptions: ChoiceOption[] = [
    { value: ExpertiseType.BEGINNER, label: 'profile.edit-account.expertise.options.BEGINNER' },
    { value: ExpertiseType.INTERMEDIATE, label: 'profile.edit-account.expertise.options.INTERMEDIATE' },
    { value: ExpertiseType.EXPERT, label: 'profile.edit-account.expertise.options.EXPERT' },
  ];

  pseudo = '';
  expertise: ExpertiseType | null = null;
  /** Photo envoyée pendant cette édition, pas encore rattachée au compte. */
  newPicture: UploadFileResponse | null = null;

  /** Clé du message sous le champ du nom, ou `null`. */
  pseudoErrorKey: string | null = null;
  uploading = false;
  /** La photo n'a pas pu partir : dit sous le lien, on peut réessayer. */
  pictureFailed = false;
  saving = false;
  /** Mise à jour refusée : dit au-dessus du bouton. */
  saveFailed = false;

  constructor(
    private userApi: UserApi,
    private fileApi: FileApi,
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    // À chaque ouverture (ou nouveau compte), on repart de ce qui est enregistré.
    if (changes['user'] || (changes['isOpen'] && this.isOpen)) {
      this.reset();
    }
  }

  /** Remet le formulaire sur le compte affiché, sans message ni photo en attente. */
  reset() {
    this.pseudo = this.user?.pseudo ?? '';
    this.expertise = this.user?.expertise ?? null;
    this.newPicture = null;
    this.pseudoErrorKey = null;
    this.pictureFailed = false;
    this.saveFailed = false;
  }

  /** Photo affichée : la nouvelle si on vient d'en choisir une, sinon celle du compte. */
  get pictureUrl(): string | null {
    if (this.newPicture?.cdnUrl) return this.newPicture.cdnUrl;
    const files = this.user?.files;
    return files?.length ? ((files[files.length - 1] as any).cdnUrl ?? null) : null;
  }

  get initial(): string {
    return userInitial({ ...(this.user ?? {}), pseudo: this.pseudo } as Partial<User>);
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.uploading = true;
    this.pictureFailed = false;
    try {
      const request = new UploadFileRequest();
      request.file = file;
      request.fileName = file.name;
      request.fileType = file.type as FileType;
      this.newPicture = await this.fileApi.uploadFile(request);
    } catch (e) {
      console.error('EditUserToastComponent: failed to upload picture', e);
      this.pictureFailed = true;
    } finally {
      this.uploading = false;
      // Le même fichier peut être choisi à nouveau après un échec.
      input.value = '';
    }
  }

  /** Message à afficher sous le nom, ou `null` s'il convient. */
  validatePseudo(): string | null {
    const pseudo = (this.pseudo ?? '').trim();
    if (!pseudo) return 'profile.edit-account.pseudo.required';
    if (pseudo.length > PSEUDO_MAX_LENGTH) return 'profile.edit-account.pseudo.too-long';
    return null;
  }

  async submit() {
    if (this.saving || this.uploading) return;
    this.pseudoErrorKey = this.validatePseudo();
    if (this.pseudoErrorKey) {
      document.getElementById('edit-user-pseudo')?.focus();
      return;
    }
    this.saving = true;
    this.saveFailed = false;
    try {
      const user = this.user;
      if (this.newPicture && user) {
        // Une seule photo par compte : on détache les anciennes avant de rattacher la nouvelle.
        for (const oldFile of user.files ?? []) {
          await this.fileApi.updateExternalFile({ uid: oldFile.uid.toString(), userUid: null });
        }
        const attach = new UpdateExternalFileRequest();
        attach.uid = this.newPicture.externalFileUid;
        attach.userUid = user.uid;
        await this.fileApi.updateExternalFile(attach);
      }
      const request = new UpdateUserRequest();
      request.pseudo = this.pseudo.trim();
      if (this.expertise) request.expertise = this.expertise;
      await this.userApi.update(request);
      this.onClose(true);
    } catch (e) {
      // Mise à jour refusée : la feuille reste ouverte pour réessayer.
      console.error('EditUserToastComponent: failed to update user', e);
      this.saveFailed = true;
    } finally {
      this.saving = false;
    }
  }
}
